import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
} from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import {
  Control,
  FieldErrors,
  FormProvider,
  useForm,
  UseFormWatch,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { ErrorType, FormProps } from "@/types";
import {
  useCreateEventMutation,
  useUpdateEventMutation,
} from "@/services/eventApi";
import { useToast } from "@/components/ui/use-toast";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Event } from "@/types/event";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import EventGeneralInfoForm from "./general-info-form";
import EventAvailabilityForm from "./availability-form";
import EventSlotsAndSchedule from "./slots-and-schedule";
import EventAddOns from "./add-ons";
import EventPricingAndRefund from "./pricing-and-refund";
import { format } from "date-fns";
import { EventBookingType } from "@/types/enums";
// Define the shared form props interface for all child components
interface FormStepProps {
  control: Control<Event>;
  errors: FieldErrors<Event>;
  watch: UseFormWatch<Event>;
  setValue: UseFormSetValue<Event>;
  trigger?: UseFormTrigger<Event>;
  action: 'add' | 'edit';
  open: boolean;
}

const stepContentComponents = [
  EventGeneralInfoForm,
  EventAvailabilityForm,
  EventSlotsAndSchedule,
  EventAddOns,
  EventPricingAndRefund,
];

const getStepContent = (step: number, formProps: FormStepProps) => {
  const Component = stepContentComponents[step - 1];
  return Component ? <Component {...formProps} action={formProps.action} /> : null;
};

const steps = [
  { label: "General Information", key: 1 },
  { label: "Availability", key: 2 },
  { label: "Slots & Schedule", key: 3 },
  { label: "Add Ons", key: 4 },
  { label: "Pricing & Refund", key: 5 },
];

const EventForm = ({ isOpen, setOpen, action, setAction, data, setData, refetch }: FormProps<Event>) => {
  const [activeStep, setActiveStep] = useState(1);
  
  // Add default values for all form fields
  const initialValues: Event = {
    name: "",
    facility_name: "",
    is_shareable: false,
    booking_type: EventBookingType.FullyBooked,
    color: "",
    max_attendees: 0,
    location: "",
    description: "",
    start_date: format(new Date(), "yyyy-MM-dd"),
    end_date: format(new Date(), "yyyy-MM-dd"),
  };

  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();

  const form = useForm<Event>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    trigger,
    formState: { isSubmitting, errors },
  } = form;

  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  useEffect(() => {
    if (action == "edit" && data) {
      reset(data as Event);
    } else if (action == "add") {
      reset(initialValues);
    }
  }, [action, data, reset]);

  const handleClose = () => {
    clearErrors();
    reset(initialValues);
    setData(undefined);
    setActiveStep(1);
    setOpen(false);
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const onSubmit = async (input: Event) => {
    const payload = { org_id: orgId, ...input };

    try {
      if (action === "add") {
        await createEvent(payload).unwrap();
        toast({
          variant: "success",
          title: "Event created successfully",
        });
        refetch();
      } else if (action === "edit") {
        await updateEvent({ ...payload, id: data?.id as number }).unwrap();
        toast({
          variant: "success",
          title: "Event updated Successfully",
        });
        refetch();
      }
      handleClose();
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const handleNext = async () => {
    const isValid = await trigger();
    if (isValid && activeStep < steps.length) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const isLastStep = activeStep === steps.length;
  const isFirstStep = activeStep === 1;

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1000px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4"
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-start gap-3">
                  <button type="button" onClick={handleClose}>
                    <i className="fa fa-xmark text-xl"></i>
                  </button>
                  <div>
                    <h1 className="font-medium">
                      {action == "add" ? "Create" : "Edit"} Event
                    </h1>
                    <CompactStepIndicator
                      activeStep={activeStep}
                      steps={steps}
                    />
                  </div>
                </div>

                  <div className="flex justify-center space-x-[20px]">
                    {!isFirstStep && (
                      <Button
                        type="button"
                        className="w-[100px] text-center flex items-center gap-2 border-primary"
                        variant={"outline"}
                        onClick={handlePrevious}
                      >
                        <i className="fa fa-arrow-left"></i>
                        Previous
                      </Button>
                    )}

                    {!isLastStep ? (
                      <Button
                        type="button"
                        className="w-[100px] bg-primary text-black text-center flex items-center gap-2"
                        onClick={handleNext}
                      >
                        Next
                      </Button>
                    ) : (
                      <LoadingButton
                        type="submit"
                        className="w-[120px] bg-primary text-black text-center flex items-center gap-2"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {!isSubmitting && (
                          <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                        )}
                        {action == "add" ? "Save" : "Update"}
                      </LoadingButton>
                    )}
                  </div>
                </div>
            </SheetHeader>


            <div className="min-h-[400px] py-4 px-6">
              {getStepContent(activeStep, {
                control,
                errors,
                watch,
                setValue,
                trigger,
                action,
                open: isOpen,
              })}
            </div>

          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default EventForm;
